import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import HomeIcon from "../assets/icons/thinkIcons/Home.png";
import DeliveryETAImage from "../assets/img/delivery_ETA.png";
import VanImg from "../assets/img/VanImg.png";
import { Minus, Plus, ChevronDown } from "lucide-react";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

export default function DeliveryETA() {
  const navigate = useNavigate();
  // const trafficOptions = ["Low", "Medium", "High"];

  // Form states
  const [timeOfDay, setTimeOfDay] = useState("Morning");
  const [weather, setWeather] = useState("Clear");
  const [vehicle, setVehicle] = useState("Bike");
  const [traffic, setTraffic] = useState("Low");
  const [distance, setDistance] = useState(0);
  const [prepTime, setPrepTime] = useState(0);
  const [experience, setExperience] = useState(0);

  // Result states
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleHomeClick = () => {
    navigate("/user/dashboard");
  };

  const handlePredict = async () => {
    setLoading(true);
    setPrediction(null);

    const payload = {
      Distance_km: distance,
      Time_of_Day: timeOfDay,
      Weather: weather,
      Vehicle_Type: vehicle,
      Preparation_Time_min: prepTime,
      Traffic_Level: traffic,
      Courier_Experience_yrs: experience,
    };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/delivery/predict`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();
      setPrediction(data);
    } catch (error) {
      console.error("Prediction error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Sample chart data
  const chartData = [
    { name: "Distance", value: distance },
    { name: "Prep Time", value: prepTime },
    { name: "Experience", value: experience },
  ];

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-10">
      {/* Header Row */}
      <div className="flex justify-end">
        <button
          className="flex items-center gap-2 border px-3 py-2 rounded-full cursor-pointer"
          onClick={handleHomeClick}
        >
          <img src={HomeIcon} className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="text-xs sm:text-sm">Home</span>
        </button>
      </div>

      {/* Top Card */}
      <div className="border-1 rounded-md mt-4 flex flex-col sm:flex-row items-center sm:items-start p-3 gap-4 border-blue-custom">
        <img
          src={DeliveryETAImage}
          alt="Advisory Tool"
          className="w-full sm:w-[200px] h-auto sm:h-[120px] object-cover rounded-md"
        />
        <div className="">
          <h2 className="textcolor font-semibold text-lg sm:text-xl">
            Delivery ETA
          </h2>
          <p className="textsec text-sm sm:text-base">
            Smarter Predictions for delivery timelines
          </p>
        </div>
      </div>

      {/* Form Title */}
      <h3 className="mt-8 sm:mt-10 textcolor font-semibold text-sm sm:text-lg border-l-4 pl-2 border-blue-custom">
        Enter the delivery parameters to estimate the delivery time.
      </h3>

      {/* Form Fields */}
      <div className="mt-8 sm:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Distance */}
        <div>
          <label className="block mb-2 text-sm sm:text-lg font-semibold">
            Distance (km)
          </label>
          <div className="relative flex items-center border-1 border-blue-custom rounded-md overflow-hidden">
            <button
              type="button"
              className="absolute left-2 text-gray-600 hover:text-gray-900"
              onClick={() =>
                setDistance((prev) => Math.max(0, +(prev - 0.5).toFixed(2)))
              }
            >
              <Minus size={20} />
            </button>
            <input
              type="number"
              className="w-full h-[50px] sm:h-[62px] pl-10 pr-10 text-center text-sm border-0 focus:outline-none placeholder:text-gray-400"
              value={distance !== undefined ? distance.toFixed(2) : "0.00"}
              readOnly
            />
            <button
              type="button"
              className="absolute right-2 text-gray-600 hover:text-gray-900"
              onClick={() => setDistance((prev) => +(prev + 0.5).toFixed(2))}
            >
              <Plus size={20} />
            </button>
          </div>
        </div>

        {/* Time of Day */}
        <div>
          <label className="block mb-2 text-sm sm:text-lg font-semibold">
            Time of the day
          </label>
          <div className="relative">
            <select
              className="w-full h-[50px] sm:h-[62px] pr-10 pl-3 text-sm border-1 border-blue-custom rounded-md focus:outline-none appearance-none"
              value={timeOfDay}
              onChange={(e) => setTimeOfDay(e.target.value)}
            >
              <option>Morning</option>
              <option>Afternoon</option>
              <option>Evening</option>
              <option>Night</option>
            </select>
            <ChevronDown
              size={20}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none"
            />
          </div>
        </div>

        {/* Weather */}
        <div>
          <label className="block mb-2 text-sm sm:text-lg font-semibold">
            Weather
          </label>
          <div className="relative">
            <select
              className="w-full h-[50px] sm:h-[62px] pr-10 pl-3 text-sm border-1 border-blue-custom rounded-md focus:outline-none appearance-none"
              value={weather}
              onChange={(e) => setWeather(e.target.value)}
            >
              <option>Clear</option>
              <option>Foggy</option>
              <option>Rainy</option>
              <option>Windy</option>
              <option>Snowy</option>
            </select>
            <ChevronDown
              size={20}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none"
            />
          </div>
        </div>

        {/* Vehicle Type */}
        <div>
          <label className="block mb-2 text-sm sm:text-lg font-semibold">
            Vehicle Type
          </label>
          <div className="relative">
            <select
              className="w-full h-[50px] sm:h-[62px] pr-10 pl-3 text-sm border-1 border-blue-custom rounded-md focus:outline-none appearance-none"
              value={vehicle}
              onChange={(e) => setVehicle(e.target.value)}
            >
              <option>Bike</option>
              <option>Scooter</option>
              <option>Car</option>
            </select>
            <ChevronDown
              size={20}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none"
            />
          </div>
        </div>

        {/* Preparation Time */}
        <div>
          <label className="block mb-2 text-sm sm:text-lg font-semibold">
            Preparation Time (min)
          </label>
          <div className="relative flex items-center border-1 border-blue-custom rounded-md overflow-hidden">
            <button
              type="button"
              className="absolute left-2 text-gray-600 hover:text-gray-900"
              onClick={() => setPrepTime((prev) => Math.max(0, prev - 5))}
            >
              <Minus size={20} />
            </button>
            <input
              type="number"
              className="w-full h-[50px] sm:h-[62px] pl-10 pr-10 text-center text-sm border-0 focus:outline-none placeholder:text-gray-400"
              value={prepTime.toFixed(2)}
              readOnly
            />
            <button
              type="button"
              className="absolute right-2 text-gray-600 hover:text-gray-900"
              onClick={() => setPrepTime((prev) => prev + 5)}
            >
              <Plus size={20} />
            </button>
          </div>
        </div>

        {/* Traffic Level */}
        <div>
          <label className="block mb-2 text-sm sm:text-lg font-semibold">
            Traffic Level
          </label>
          <div className="relative">
            <select
              className="w-full h-[50px] sm:h-[62px] pr-10 pl-3 text-sm border-1 border-blue-custom rounded-md focus:outline-none appearance-none"
              value={traffic}
              onChange={(e) => setTraffic(e.target.value)}
            >
              <option>Low</option>
              <option>Medium</option>
              <option>High</option>
            </select>
            <ChevronDown
              size={20}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none"
            />
          </div>
        </div>

        {/* Courier Experience */}
        <div>
          <label className="block mb-2 text-sm sm:text-lg font-semibold">
            Courier Experience (Year)
          </label>
          <div className="relative flex items-center border-1 border-blue-custom rounded-md overflow-hidden">
            <button
              type="button"
              className="absolute left-2 text-gray-600 hover:text-gray-900"
              onClick={() =>
                setExperience((prev) => Math.max(0, +(prev - 0.5).toFixed(1)))
              }
            >
              <Minus size={20} />
            </button>
            <input
              type="number"
              className="w-full h-[50px] sm:h-[62px] pl-10 pr-10 text-center text-sm border-0 focus:outline-none placeholder:text-gray-400"
              value={experience}
              readOnly
            />
            <button
              type="button"
              className="absolute right-2 text-gray-600 hover:text-gray-900"
              onClick={() => setExperience((prev) => +(prev + 0.5).toFixed(1))}
            >
              <Plus size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-center mt-8 mb-8">
        <button
          className="bgbtn1 textwhite px-4 sm:px-6 py-2 rounded-lg shadow font-semibold"
          onClick={handlePredict}
          disabled={loading}
        >
          {loading ? "Predicting..." : "Predict to delivery"}
        </button>
      </div>

      {/* Prediction Result Section */}
      {prediction && (
        <div className="mt-10 border-1 border-blue-custom rounded-lg p-4 sm:p-6 lg:p-8 shadow-lg bg-white max-w-full lg:max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <h3 className="text-xl sm:text-2xl font-bold textcolor tracking-wide">
              Prediction Result :
            </h3>
            <div className="flex justify-center">
              <div className="bgwhite textcolor font-semibold rounded-md px-3 py-1 text-base sm:text-lg shadow-md border border-blue-300 mb-3">
                Predicted Delivery Time:{" "}
                <span className="font-bold textprimary">
                  {Number(prediction.predicted_delivery_time).toFixed(2)}{" "}
                  minutes
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 overflow-x-auto">
            <img
              src={VanImg}
              alt="Van"
              className="w-full sm:w-[800px] lg:w-[1249px] h-auto sm:h-[170px] object-cover rounded"
            />
          </div>

          {/* Input Summary Table */}
          <div className="mt-8 overflow-x-auto">
            <h4 className="font-semibold text-lg mb-3 text-gray-800">
              Input Summary
            </h4>
            <table className="table-auto border-collapse border border-gray-300 w-full text-sm sm:text-base shadow-sm rounded-md overflow-hidden">
              <tbody>
                {[
                  ["Distance", `${distance} km`],
                  ["Time of Day", timeOfDay],
                  ["Weather", weather],
                  ["Vehicle", vehicle],
                  ["Prep Time", `${prepTime} min`],
                  ["Traffic", traffic],
                  ["Experience", `${experience} years`],
                ].map(([label, value]) => (
                  <tr key={label} className="even:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2 font-medium text-gray-700">
                      {label}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-gray-900">
                      {value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Numeric Input Visualization */}
          <div className="mt-12">
            <h4 className="font-semibold text-lg mb-4 text-gray-800">
              Numeric Input Visualization
            </h4>
            <div className="w-full h-[200px] sm:h-[260px] bg-white rounded-md shadow-inner p-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#f9fafb",
                      borderRadius: "6px",
                      borderColor: "#cbd5e1",
                    }}
                  />
                  <Legend
                    wrapperStyle={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#334155",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#3b82f6"
                    strokeWidth={3}
                    activeDot={{ r: 8 }}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// import { Listbox } from "@headlessui/react";
// import { ChevronDownIcon, CheckIcon } from "@heroicons/react/20/solid";

{
  /* Important Dropdown */
}
{
  /* <div>
          <label className="block mb-2 text-md font-semibold">
            Traffic Level
          </label>
          <Listbox value={traffic} onChange={setTraffic}>
            <div className="relative w-full">
              <Listbox.Button className="relative w-full h-[62px] cursor-pointer rounded-md bg-white border-1 border-blue-custom py-3 pl-4 pr-10 text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                <span className="block truncate">{traffic}</span>
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                  <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                </span>
              </Listbox.Button>

              <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {trafficOptions.map((option) => (
                  <Listbox.Option
                    key={option}
                    value={option}
                    className={({ active }) =>
                      `relative cursor-pointer select-none py-3 pl-10 pr-4 ${
                        active ? "bg-blue-100 text-blue-900" : "text-gray-900"
                      }`
                    }
                  >
                    {({ selected }) => (
                      <>
                        <span
                          className={`block truncate ${
                            selected ? "font-medium" : "font-normal"
                          }`}
                        >
                          {option}
                        </span>
                        {selected && (
                          <span className="absolute left-3 inset-y-0 flex items-center text-blue-600">
                            <CheckIcon className="h-5 w-5" />
                          </span>
                        )}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </div>
          </Listbox>
        </div> */
}
